import React, { useEffect, useState } from "react";
import Layout from "../shared/Layout";
import { supabase } from "../../supabaseClient";
import { useAuth } from "../authentication/AuthContext";
import MDEditor from "@uiw/react-md-editor";
import {
  extractAllContent,
  extractDescription,
  extractImage,
  extractTitle,
} from "../shared/utils/ExtractionUtils";
import { TaskLink } from "./models/TaskLink";
import * as cheerio from "cheerio";
import { useNavigate } from "react-router-dom";
import { Task } from "./models/Task";
import { getGeminiCompletion, getGeminiEmbedding } from "../ai/GeminiUtils";
import { useShowTempErrorMessage } from "../shared/hooks/useUtilityContext";
import { logAnalyticsEvent } from "../engagement/Amplitude";

export default function CreateTask() {
  const { user } = useAuth();
  const [title, setTitle] = useState(localStorage.getItem("task_title") ?? "");
  const [description, setDescription] = useState(
    localStorage.getItem("task_description") ?? ""
  );
  const [snippet, setSnippet] = useState(
    localStorage.getItem("task_snippet") ?? ""
  );
  const [selectedLanguage, setSelectedLanguage] = useState(localStorage.getItem("task_language") ?? "javascript");
  const [tags, setTags] = useState<string[]>(
    localStorage.getItem("task_tags")
      ? JSON.parse(localStorage.getItem("task_tags")!)
      : []
  );
  const [addingTask, setAddingTask] = useState(false);
  const [links, setLinks] = useState<string[]>([]);
  const [link, setLink] = useState("");
  const snippetRef = React.createRef<HTMLTextAreaElement>();
  const [generatingMetadata, setGeneratingMetadata] = useState(false);
  const navigate = useNavigate();
  const showTempErrorMessage = useShowTempErrorMessage();

  useEffect(() => {
    localStorage.setItem("task_title", title);
  }, [title]);

  useEffect(() => {
    localStorage.setItem("task_description", description);
  }, [description]);

  useEffect(() => {
    localStorage.setItem("task_snippet", snippet);
  }, [snippet]);

  useEffect(() => {
    localStorage.setItem("task_tags", JSON.stringify(tags));
  }, [tags]);

  useEffect(() => {
    localStorage.setItem("task_language", selectedLanguage);
  }, [selectedLanguage]);

  const handleInput = () => {
    const textarea = snippetRef.current;

    if (!textarea) return;
    const maxHeight = 1000; // Set your desired max height here

    textarea.style.height = "auto";
    const newHeight = textarea.scrollHeight;
    if (newHeight > maxHeight) {
      textarea.style.height = maxHeight + "px";
      textarea.style.overflowY = "auto"; // Enable scrolling
    } else {
      textarea.style.height = newHeight + "px";
    }
  };

  useEffect(() => {
    handleInput();
  }, [snippet]);

  return (
    <Layout>
      <div className="p-4">
        <div className="flex gap-4 items-center">
          <div className="text-md font-bold">Snippet</div>
          <select
            title="Language"
            className="select-bordered select"
            value={selectedLanguage}
            onChange={(e) => {
              setSelectedLanguage(e.target.value);
            }}
          >
            {languages.map((language) => (
              <option
                key={language}
                value={language}
              >
                {language}
              </option>
            ))}
          </select>
        </div>
        
        <textarea
          ref={snippetRef}
          className="textarea textarea-bordered mt-2 w-full leading-5 font-mono"
          value={snippet}
          onChange={(e) => setSnippet(e.target.value)}
          placeholder="Enter your code snippet here"
        ></textarea>
        <label className="text-sm font-light">
          This code will be inserted when the snippet is used.
        </label>
        <div className="flex justify-end items-center gap-4">
          {generatingMetadata && (
            <div className="loading loading-dots loading-xs"></div>
          )}
          <button
            className="btn btn-outline btn-sm"
            disabled={snippet.trim().length === 0}
            onClick={async () => {
              setGeneratingMetadata(true);
              try {
                const contentToSummarize = snippet;
                const newTitle = await getGeminiCompletion({
                  prompt: `Give me a concise and imperative title for this code snippet:
        \n\n${contentToSummarize}`,
                });
                const newDescription = await getGeminiCompletion({
                  prompt: `Give me a concise description for this code snippet:
        \n\n${contentToSummarize}`,
                });
                const newTags = await getGeminiCompletion({
                  prompt: `Give me a comma separated list of tags for this code snippet:
        \n\n${contentToSummarize}`,
                });
                setTitle(newTitle);
                setDescription(newDescription);
                setTags(newTags.split(",").map((tag) => tag.trim()));
                setGeneratingMetadata(false);
              } catch (error) {
                console.error("Error generating metadata: ", error);
                showTempErrorMessage(
                  "Error generating metadata. Please try again later."
                );
                setGeneratingMetadata(false);
              }
            }}
          >
            Generate Metadata
          </button>
        </div>
        {/* <MDEditor
          title="New Task"
          ref={messageRef}
          value={snippet}
          className="my-4"
          height={"50vh"}
          minHeight={100}
          visibleDragbar={false}
          maxHeight={800}
          onChange={(e) => {
            setSnippet(e ?? "");
          }}
        ></MDEditor> */}
        <div className="text-md font-bold mt-8">Snippet Title</div>
        <div className="prose text-sm font-light">
          <span className="prose text-sm font-light">
            Use the{" "}
            <a
              href="https://en.wikipedia.org/wiki/Imperative_mood"
              target="_blank"
              rel="noopener noreferrer"
            >
              imperative mood
            </a>{" "}
            and keep it concise but specific.
          </span>
        </div>
        <input
          placeholder="Title"
          type="text"
          value={title}
          maxLength={100}
          onChange={(e) => setTitle(e.target.value)}
          className="input input-bordered w-full mt-2"
        ></input>
        <div className="text-md font-bold mt-8">
          Snippet Description (optional)
        </div>
        <textarea
          placeholder="Description"
          value={description}
          maxLength={300}
          onChange={(e) => setDescription(e.target.value)}
          className="textarea textarea-bordered mt-2 w-full"
        ></textarea>
        <div className="form-control mb-8">
          <span className="text-md font-bold">
            <span>Tags</span>
          </span>
          <label className="text-sm font-light">
            Tags help others find your snippet. Separate tags with commas.
          </label>
          <input
            type="text"
            placeholder="Enter tags (comma-separated)"
            className="input input-bordered mt-2"
            value={tags}
            onChange={(e) =>
              setTags(
                e.target.value.split(",").map((tag) => tag.toLowerCase().trim())
              )
            }
          />
        </div>
        <div>
          <div className="text-md font-bold">
            <span>Links</span>
          </div>
          <div className="prose text-sm font-light">
            Add links that help explain the snippet.
          </div>
          <div className="flex gap-4">
            <input
              type="text"
              placeholder="Enter a link"
              value={link}
              onChange={(e) => setLink(e.target.value)}
              className="input input-bordered mt-2 w-full"
            ></input>
            <button
              className="btn btn-accent mt-2"
              type="button"
              disabled={links.length > 2}
              onClick={() => {
                setLinks([...links, link]);
                setLink("");
              }}
            >
              Add Link
            </button>
          </div>
          {links.length > 0 && (
            <div className="mt-4">
              <div className="text-md font-bold mt-8">Links Added</div>
              <div className="flex flex-col gap-2 mt-2">
                {links.map((link, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 w-full"
                  >
                    <a
                      href={link}
                      className="grow"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link}
                    </a>
                    <button
                      className="btn btn-outline"
                      type="button"
                      onClick={() => {
                        setLinks(links.filter((_, i) => i !== index));
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          title="Create Snippet"
          onClick={async () => {
            logAnalyticsEvent("Create Snippet");
            console.log("Title: ", title);
            console.log("Tags: ", tags);
            const embedding = await getGeminiEmbedding(title);
            setAddingTask(true);
            let newTask: Task = {
              user_id: user!.id,
              created_at: new Date().toISOString(),
              title,
              description,
              tags,
              snippet,
              embedding,
              language: selectedLanguage,
              updated_at: new Date().toISOString(),
              updated_by: user!.id,
            };
            const { data, error } = await supabase
              .from("tasks")
              .insert(newTask)
              .select("*");
            let linkDetails: TaskLink[] = [];
            if (links.length > 0) {
              const promises = links.map((link) => extractAllContent(link));
              const results = await Promise.all(promises);
              results.forEach((result, index) => {
                const url = links[index];
                const $ = cheerio.load(result);
                const title = extractTitle($);
                const description = extractDescription($);
                const image = extractImage($);
                linkDetails.push({
                  url,
                  title,
                  description,
                  image,
                  task_id: data![0].id,
                  user_id: user!.id,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                });
              });
              if (linkDetails.length > 0) {
                const promises = linkDetails.map((link) =>
                  supabase.from("links").insert(link)
                );
                await Promise.all(promises);
              }
            }
            localStorage.removeItem("task_title");
            localStorage.removeItem("task_description");
            localStorage.removeItem("task_tags");
            localStorage.removeItem("task_snippet");
            setAddingTask(false);
            navigate(`/snippet/${data![0].id}`);
          }}
          disabled={addingTask || !title || !description}
          className="btn btn-primary w-full my-4"
        >
          {addingTask && <div className="loading loading-dots mr-4"></div>}
          Create Snippet
        </button>
      </div>
    </Layout>
  );
}

export const languages = [
  "",
  "abap",
  "abnf",
  "actionscript",
  "ada",
  "agda",
  "al",
  "antlr4",
  "apacheconf",
  "apex",
  "apl",
  "applescript",
  "aql",
  "arduino",
  "arff",
  "asciidoc",
  "asm6502",
  "asmatmel",
  "aspnet",
  "autohotkey",
  "autoit",
  "avisynth",
  "avroIdl",
  "bash",
  "basic",
  "batch",
  "bbcode",
  "bicep",
  "birb",
  "bison",
  "bnf",
  "brainfuck",
  "brightscript",
  "bro",
  "bsl",
  "c",
  "cfscript",
  "chaiscript",
  "cil",
  "clike",
  "clojure",
  "cmake",
  "cobol",
  "coffeescript",
  "concurnas",
  "coq",
  "cpp",
  "crystal",
  "csharp",
  "cshtml",
  "csp",
  "cssExtras",
  "css",
  "csv",
  "cypher",
  "d",
  "dart",
  "dataweave",
  "dax",
  "dhall",
  "diff",
  "django",
  "dnsZoneFile",
  "docker",
  "dot",
  "ebnf",
  "editorconfig",
  "eiffel",
  "ejs",
  "elixir",
  "elm",
  "erb",
  "erlang",
  "etlua",
  "excelFormula",
  "factor",
  "falselang",
  "firestoreSecurityRules",
  "flow",
  "fortran",
  "fsharp",
  "ftl",
  "gap",
  "gcode",
  "gdscript",
  "gedcom",
  "gherkin",
  "git",
  "glsl",
  "gml",
  "gn",
  "goModule",
  "go",
  "graphql",
  "groovy",
  "haml",
  "handlebars",
  "haskell",
  "haxe",
  "hcl",
  "hlsl",
  "hoon",
  "hpkp",
  "hsts",
  "http",
  "ichigojam",
  "icon",
  "icuMessageFormat",
  "idris",
  "iecst",
  "ignore",
  "inform7",
  "ini",
  "io",
  "j",
  "java",
  "javadoc",
  "javadoclike",
  "javascript",
  "javastacktrace",
  "jexl",
  "jolie",
  "jq",
  "jsExtras",
  "jsTemplates",
  "jsdoc",
  "json",
  "json5",
  "jsonp",
  "jsstacktrace",
  "jsx",
  "julia",
  "keepalived",
  "keyman",
  "kotlin",
  "kumir",
  "kusto",
  "latex",
  "latte",
  "less",
  "lilypond",
  "liquid",
  "lisp",
  "livescript",
  "llvm",
  "log",
  "lolcode",
  "lua",
  "magma",
  "makefile",
  "markdown",
  "markupTemplating",
  "markup",
  "matlab",
  "maxscript",
  "mel",
  "mermaid",
  "mizar",
  "mongodb",
  "monkey",
  "moonscript",
  "n1ql",
  "n4js",
  "nand2tetrisHdl",
  "naniscript",
  "nasm",
  "neon",
  "nevod",
  "nginx",
  "nim",
  "nix",
  "nsis",
  "objectivec",
  "ocaml",
  "opencl",
  "openqasm",
  "oz",
  "parigp",
  "parser",
  "pascal",
  "pascaligo",
  "pcaxis",
  "peoplecode",
  "perl",
  "phpExtras",
  "php",
  "phpdoc",
  "plsql",
  "powerquery",
  "powershell",
  "processing",
  "prolog",
  "promql",
  "properties",
  "protobuf",
  "psl",
  "pug",
  "puppet",
  "pure",
  "purebasic",
  "purescript",
  "python",
  "q",
  "qml",
  "qore",
  "qsharp",
  "r",
  "racket",
  "reason",
  "regex",
  "rego",
  "renpy",
  "rest",
  "rip",
  "roboconf",
  "robotframework",
  "ruby",
  "rust",
  "sas",
  "sass",
  "scala",
  "scheme",
  "scss",
  "shellSession",
  "smali",
  "smalltalk",
  "smarty",
  "sml",
  "solidity",
  "solutionFile",
  "soy",
  "sparql",
  "splunkSpl",
  "sqf",
  "sql",
  "squirrel",
  "stan",
  "stylus",
  "swift",
  "systemd",
  "t4Cs",
  "t4Templating",
  "t4Vb",
  "tap",
  "tcl",
  "textile",
  "toml",
  "tremor",
  "tsx",
  "tt2",
  "turtle",
  "twig",
  "typescript",
  "typoscript",
  "unrealscript",
  "uorazor",
  "uri",
  "v",
  "vala",
  "vbnet",
  "velocity",
  "verilog",
  "vhdl",
  "vim",
  "visualBasic",
  "warpscript",
  "wasm",
  "webIdl",
  "wiki",
  "wolfram",
  "wren",
  "xeora",
  "xmlDoc",
  "xojo",
  "xquery",
  "yaml",
  "yang",
  "zig",
];